import { useCallback, useContext, useRef, useState } from 'react';

import {
  BULK_CLOCK_OUT_USER_IDS_LIMIT,
  bulkClockOutRequest,
  getCheckInWorkersRequest,
} from '@vyce/core/src/api/time';
import { BulkClockOutDTO, BulkClockOutSkipReason, CheckInWorkerDTO } from '@vyce/core/src/api/types';
import { toUkWallClock } from '@vyce/core/src/utils/dates';

import { NotificationContext } from '../../../contexts/notificationContext';
import { TimestampType } from '../types';

const PAGE_SIZE = 100;
// Stops a runaway loop if the backend ever reports a count it can't page through.
const MAX_PAGES = 100;

export interface BulkClockOutRequestParams {
  /** Always explicit: an empty or missing site_ids would mean every location the caller manages. */
  siteIds: string[];
  timestamp: TimestampType;
  /** Human summary of the chosen locations, for the preview header. */
  locationsLabel: string;
}

export interface BulkClockOutResult {
  clockedOutCount: number;
  skipped: { userId: string; name: string; reason: BulkClockOutSkipReason }[];
}

interface Props {
  companyId?: string;
  /** Locations the manager may clock workers on; null = every location. */
  allowedLocationIds: string[] | null;
  onCompleted: () => void;
}

const chunk = <T>(items: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
};

const workerName = (worker: CheckInWorkerDTO) =>
  `${worker.first_name ?? ''} ${worker.last_name ?? ''}`.trim();

/**
 * Preview and submit for "clock out all clocked-in users".
 *
 * The preview is the checked-in list for the same locations, every page loaded
 * and every row ticked. With everyone still ticked the request leaves user_ids
 * out and the backend clocks out whoever is on those locations at that moment;
 * unticking anyone switches to an explicit user_ids list.
 */
export const useBulkClockOut = ({ companyId, allowedLocationIds, onCompleted }: Props) => {
  const { handleServerError } = useContext(NotificationContext);
  const [isOpen, setIsOpen] = useState(false);
  const [params, setParams] = useState<BulkClockOutRequestParams | null>(null);
  const [workers, setWorkers] = useState<CheckInWorkerDTO[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<BulkClockOutResult | null>(null);
  const requestIdRef = useRef(0);

  const loadWorkers = useCallback(
    async (siteIds: string[]) => {
      if (!companyId || !siteIds.length) {
        return;
      }
      const requestId = ++requestIdRef.current;
      setLoading(true);
      setWorkers([]);
      setSelectedIds(new Set());
      try {
        const items: CheckInWorkerDTO[] = [];
        for (let page = 0; page < MAX_PAGES; page++) {
          const { data } = await getCheckInWorkersRequest({
            companyId,
            payload: { site_ids: siteIds, offset: page * PAGE_SIZE, limit: PAGE_SIZE },
          });
          items.push(...data.items);
          if (!data.items.length || items.length >= data.count) {
            break;
          }
        }
        if (requestId !== requestIdRef.current) {
          return;
        }

        // One row per worker, and only on locations this manager can clock on:
        // the bulk endpoint silently drops the rest, so showing them would promise
        // a clock-out that never happens.
        const byUser = new Map<string, CheckInWorkerDTO>();
        items.forEach(item => {
          const allowed = allowedLocationIds === null || allowedLocationIds.includes(item.site_id);
          if (allowed && !byUser.has(item.user_id)) {
            byUser.set(item.user_id, item);
          }
        });
        const unique = Array.from(byUser.values()).sort((a, b) => workerName(a).localeCompare(workerName(b)));
        setWorkers(unique);
        setSelectedIds(new Set(unique.map(item => item.user_id)));
      } catch (e) {
        if (requestId === requestIdRef.current) {
          handleServerError(e);
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [companyId, allowedLocationIds, handleServerError]
  );

  const open = useCallback(
    (nextParams: BulkClockOutRequestParams) => {
      setParams(nextParams);
      setResult(null);
      setIsOpen(true);
      loadWorkers(nextParams.siteIds);
    },
    [loadWorkers]
  );

  const close = useCallback(() => {
    requestIdRef.current++;
    setIsOpen(false);
    setLoading(false);
    if (result) {
      onCompleted();
    }
  }, [result, onCompleted]);

  const toggleWorker = useCallback((userId: string) => {
    setSelectedIds(current => {
      const next = new Set(current);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelectedIds(current =>
      current.size === workers.length ? new Set() : new Set(workers.map(item => item.user_id))
    );
  }, [workers]);

  const confirm = useCallback(async () => {
    if (!companyId || !params?.siteIds.length || !selectedIds.size) {
      return;
    }
    // "Right now" is resolved at confirm, not when the wizard was filled in.
    const date = params.timestamp instanceof Date ? params.timestamp : new Date();
    const basePayload = { site_ids: params.siteIds, timestamp: toUkWallClock(date) };
    const everyoneTicked = workers.every(item => selectedIds.has(item.user_id));
    const batches = everyoneTicked ? [undefined] : chunk(Array.from(selectedIds), BULK_CLOCK_OUT_USER_IDS_LIMIT);

    setSubmitting(true);
    const merged: BulkClockOutDTO = { clocked_out: [], skipped: [] };
    try {
      for (const userIds of batches) {
        const { data } = await bulkClockOutRequest({
          companyId,
          payload: userIds ? { ...basePayload, user_ids: userIds } : basePayload,
        });
        merged.clocked_out.push(...(data.clocked_out ?? []));
        merged.skipped.push(...(data.skipped ?? []));
      }
    } catch (e) {
      handleServerError(e);
      // A failed later batch must not hide the ones that already went through.
      if (!merged.clocked_out.length && !merged.skipped.length) {
        setSubmitting(false);
        return;
      }
    }

    const names = new Map(workers.map(item => [item.user_id, workerName(item)]));
    setResult({
      clockedOutCount: merged.clocked_out.length,
      skipped: merged.skipped.map(item => ({
        userId: item.user_id,
        name: names.get(item.user_id) || 'Unknown worker',
        reason: item.reason,
      })),
    });
    setSubmitting(false);
  }, [companyId, params, selectedIds, workers, handleServerError]);

  return {
    isOpen,
    params,
    workers,
    selectedIds,
    loading,
    submitting,
    result,
    open,
    close,
    toggleWorker,
    toggleAll,
    confirm,
  };
};
